import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type {
  GetServerSideProps,
  NextApiRequest,
  NextApiResponse,
  NextPage,
} from 'next'

import Container from '../components/Container'
import Deals from '../components/Deals'
import DealsFilters, { DealsType } from '../components/DealsFilters'
import Layout from '../components/Layout'
import { useAuthContext, useUser } from '../context/Auth'
import { useRouter } from 'next/router'
import { BasePageProps, Deal } from '../types'
import { getSettingsSSR } from '../utils/settings'
import { FiltersFormInputs } from '../components/DealsFiltersContent'

const DealsDatabase: NextPage<BasePageProps> = () => {
  const user = useUser({ redirectTo: '/login' })
  const router = useRouter()
  const authContext = useAuthContext()

  const [deals, setDeals] = React.useState<Deal[]>([])
  const [dealType, setDealType] = React.useState<DealsType>(DealsType.LIVE)

  const showCompleted = deals.some(
    (e) => e.status?.parent?.name.toLowerCase() === 'completed'
  )

  React.useEffect(() => {
    const status = router.query.status
    if (status) {
      if (status === 'completed' && !showCompleted) {
        setDealType(DealsType.LIVE)
      } else {
        setDealType(status as DealsType)
      }
    }
  }, [showCompleted])

  React.useEffect(() => {
    async function fetchDeals() {
      try {
        const res = await fetch('/api/deals', { method: 'POST' })

        if (res.redirected) {
          authContext.changeData({ user: null })
          router.push('/login')
        }

        const resData = await res.json()

        if (resData.done) {
          setDeals(resData.data as Deal[])
        }
      } catch (e) {
        console.log(e)
      }
    }
    if (user) fetchDeals()
  }, [user])

  if (!user) return null

  function onChangeType(type: DealsType) {
    setDealType(type)
    router.push(
      {
        query: { status: type },
      },
      undefined,
      { shallow: true }
    )
  }

  async function onSearch(data: FiltersFormInputs) {
    try {
      /* recording user deals filtering activity */
      authContext.onActivityEvent( 'click', { path: router.asPath, query: router.query }, JSON.stringify(data) )

      const res = await fetch('/api/deals', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      })

      if (res.redirected) {
        authContext.changeData({ user: null })
        router.push('/login')
      }

      const resData = await res.json()

      if (resData.done) {
        setDeals(resData.data as Deal[])
      }
    } catch (e) {
      console.log(e)
    }
  }

  const dataFiltered = deals.filter(
    (e) => e.status?.parent?.name.toLowerCase() === dealType
  )

  return (
    <Layout>
      <Container className="py-24">
        <h2>
          Deals Database <span className="beta-tag">Beta</span>
        </h2>
        <DealsFilters
          type={dealType}
          isCompletedLayout={dealType === 'completed'}
          onChangeType={onChangeType}
          onSearch={onSearch}
          showCompleted={showCompleted}
        />
        <div className="mt-8">
          <AnimatePresence exitBeforeEnter>
            <motion.div
              key={dealType}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              exit={{ opacity: 0 }}
            >
              <Deals
                deals={dataFiltered}
                isFilterable={false}
                showActiveBorder={false}
                isCompleted={dealType === DealsType.COMPLETED}
                showHeader
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </Container>
      <style jsx>{`
        h2 {
          @apply mb-[64px] font-inter text-[45px] font-extrabold leading-[52px] text-black md:text-[75px] md:leading-[82px];
        }

        .beta-tag {
          @apply text-2xl font-normal;
          color: #c1c1c1;
        }
      `}</style>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps<BasePageProps> =
  async function ({ req, res }) {
    try {
      const settings = await getSettingsSSR(
        req as NextApiRequest,
        res as NextApiResponse
      )
      return {
        props: {
          settings,
        },
      }
    } catch (e) {
      console.log(e)
      return {
        redirect: {
          permanent: false,
          destination: `/login?redirect=/deals-database`,
        },
        props: {},
      }
    }
  }

export default DealsDatabase
